import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form } from "react-bootstrap";
import { getToken } from "../../utilities/authHelper";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import PackageProduct from "./PackageProduct";
import { fetchPackages } from "../../utilities/AdminUtilities";

function PackageDetails() {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editable, setEditable] = useState(false);
  const [editedPackage, setEditedPackage] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isCreatingNewPackage, setIsCreatingNewPackage] = useState(false);
  const [showLinkProductPackageModal, setShowLinkProductPackageModal] =
    useState(false);

  const handleOpenLinkProductPackage = (pkg) => {
    if (pkg) {
      setSelectedPackage(pkg);
    }
    setShowLinkProductPackageModal(true);
  };

  const handleCloseLinkProductPackage = () => {
    setShowLinkProductPackageModal(false);
  };

  const handleCreateNewPackage = () => {
    setIsCreatingNewPackage(true);
    setEditedPackage({}); // Reset edited package to be empty
    setShowModal(true);
  };

  useEffect(() => {
    fetchPackages()
      .then((data) => setPackages(data))
      .catch((error) => console.error("Error fetching packages:", error));
  }, []);

  const handlePackageClick = (pkg) => {
    setSelectedPackage(pkg);
    setEditedPackage({ ...pkg });
    setShowModal(true);
  };

  const handleEditChange = (e) => {
    setEditedPackage({ ...editedPackage, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      const package_name =
        editedPackage?.package_name || selectedPackage.package_name;
      const package_type =
        editedPackage?.package_type || selectedPackage.package_type;
      const package_status =
        editedPackage?.package_status || selectedPackage.package_status;
      const details_object = JSON.stringify();
      const requestBody = JSON.stringify({
        package_name,
        package_type,
        package_status,
        details_object,
      });
      var response;
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!isCreatingNewPackage) {
        response = await fetch(
          `${apiUrl}/admin/create_or_update_package?package_id=${selectedPackage.id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getToken()}`,
            },
            body: requestBody,
          }
        );
      } else {
        response = await fetch(`${apiUrl}/admin/create_or_update_package`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: requestBody,
        });
      }

      if (!response.ok) {
        throw new Error("Failed to update package");
      }

      setShowSuccessModal(true);
      handleCloseModal();
    } catch (error) {
      console.error("Error updating package:", error);
    }
  };

  const handleCloseModal = () => {
    setEditable(false);
    setShowModal(false);
    setIsCreatingNewPackage(false);
  };

  const handleCloseSuccessModal = () => setShowSuccessModal(false);

  return (
    <>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Package Name</th>
            <th>Type</th>
            <th>Status</th>
            <th>Created By</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {packages.map((pkg) => (
            <tr key={pkg.id}>
              <td>{pkg.package_name}</td>
              <td>{pkg.package_type}</td>
              <td>{pkg.package_status}</td>
              <td>{pkg.created_by}</td>
              <td>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handlePackageClick(pkg)}
                >
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="ml-2"
                  onClick={() => handleOpenLinkProductPackage(pkg)}
                >
                  Link Product
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header>
          <Modal.Title>
            {isCreatingNewPackage
              ? "Create New Package"
              : selectedPackage?.package_name}
          </Modal.Title>
          <i
            className="fas fa-edit ml-2"
            style={{ cursor: "pointer" }}
            onClick={() => setEditable(!editable)}
          ></i>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="packageId">
              <FloatingLabel controlId="packageId" label="Package ID">
                <Form.Control
                  type="text"
                  name="id"
                  value={
                    isCreatingNewPackage ? "" : selectedPackage?.id || "N.A."
                  }
                  placeholder="Package ID"
                  disabled={true}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="packageName">
              <FloatingLabel controlId="packageName" label="Package Name">
                <Form.Control
                  type="text"
                  name="package_name"
                  value={
                    editedPackage?.package_name ||
                    (isCreatingNewPackage ? "" : selectedPackage?.package_name)
                  }
                  placeholder="Package Name"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="packageStatus">
              <FloatingLabel controlId="packageStatus" label="Package Status">
                <Form.Control
                  type="text"
                  name="package_status"
                  value={
                    editedPackage?.package_status ||
                    (isCreatingNewPackage
                      ? ""
                      : selectedPackage?.package_status || "N.A.")
                  }
                  placeholder="Package Status"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="packageType">
              <FloatingLabel controlId="packageType" label="Package Type">
                <Form.Control
                  type="text"
                  name="package_type"
                  value={
                    editedPackage?.package_type ||
                    (isCreatingNewPackage
                      ? ""
                      : selectedPackage?.package_type || "N.A.")
                  }
                  placeholder="Package Type"
                  onChange={handleEditChange}
                  disabled={!editable}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="createdBy">
              <FloatingLabel controlId="createdBy" label="Created By">
                <Form.Control
                  type="text"
                  name="created_by"
                  value={
                    isCreatingNewPackage
                      ? ""
                      : selectedPackage?.created_by || "N.A."
                  }
                  placeholder="Created By "
                  onChange={handleEditChange}
                  disabled={true}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="updationTimestamp">
              <FloatingLabel
                controlId="updationTimestamp"
                label="Updation Timestamp"
              >
                <Form.Control
                  type="text"
                  name="updation_timestamp"
                  value={
                    isCreatingNewPackage
                      ? ""
                      : selectedPackage?.updation_timestamp || "N.A."
                  }
                  placeholder="Updation Timestamp"
                  onChange={handleEditChange}
                  disabled={true}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3" controlId="terminationTimestamp">
              <FloatingLabel
                controlId="terminationTimestamp"
                label="Termination Timestamp"
              >
                <Form.Control
                  type="text"
                  name="termination_timestamp"
                  value={
                    isCreatingNewPackage
                      ? ""
                      : selectedPackage?.termination_timestamp || "N.A."
                  }
                  placeholder="Termination Timestamp"
                  disabled={true}
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="creationTimestamp">
              <FloatingLabel
                controlId="creationTimestamp"
                label="Creation Timestamp"
              >
                <Form.Control
                  type="text"
                  name="creation_timestamp"
                  value={
                    isCreatingNewPackage
                      ? ""
                      : selectedPackage?.creation_timestamp || "N.A."
                  }
                  placeholder="Creation Timestamp"
                  disabled={true}
                />
              </FloatingLabel>
            </Form.Group>

            {/* <Form.Group className="mb-3" controlId="packagePriceDetails">
                  <FloatingLabel
                    controlId="packagePriceDetails"
                    label="Package Price Details"
                  >
                    <Form.Control
                      as="textarea"
                      name="package_price_details"
                      value={JSON.stringify(
                        selectedPackage?.package_price_details || {},
                        null,
                        2
                      )}
                      placeholder="Package Price Details"
                      onChange={handleEditChange}
                      disabled={!editable}
                    />
                  </FloatingLabel>
                </Form.Group> */}

            {/* Uncomment and update this section if you need to display package history or other JSON fields */}
            {/* <Form.Group className="mb-3" controlId="packageHistory">
                  <FloatingLabel controlId="packageHistory" label="Package History">
                    <Form.Control
                      as="textarea"
                      name="package_history"
                      value={JSON.stringify(selectedPackage?.package_history || {}, null, 2)}
                      placeholder="Package History"
                      onChange={handleEditChange}
                      disabled={!editable}
                    />
                  </FloatingLabel>
                </Form.Group> */}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleOpenLinkProductPackage}>
            Link New Product
          </Button>
          {editable && (
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showSuccessModal} onHide={handleCloseSuccessModal} centered>
        <Modal.Header>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Your changes have been saved successfully.</Modal.Body>
      </Modal>
      <div className="p-3 d-flex justify-content-center align-items-center">
        <Button variant="primary" onClick={handleCreateNewPackage}>
          Create New Package
        </Button>
      </div>
      {showLinkProductPackageModal && (
        <PackageProduct
          selectedPackage={selectedPackage}
          show={showLinkProductPackageModal}
          handleClose={handleCloseLinkProductPackage}
        />
      )}
    </>
  );
}

export default PackageDetails;
